"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";

export default function LoginPage() {
  const router = useRouter();
  const [userId, setUserId] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleLogin() {
    setError("");
    setLoading(true);

    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId, password }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || "Authentication failed.");
        return;
      }

      router.push("/dashboard");
    } catch (err) {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <main
      className="min-h-screen flex items-center justify-center relative overflow-hidden"
      style={{ backgroundColor: "#0B0C10", fontFamily: "'DM Mono', monospace" }}
    >
      {/* --- all your existing background decorations unchanged --- */}
      <div className="absolute inset-0 pointer-events-none" style={{
        backgroundImage: `linear-gradient(rgba(0,240,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(0,240,255,0.03) 1px, transparent 1px)`,
        backgroundSize: "40px 40px",
      }} />
      <div className="absolute pointer-events-none" style={{
        width: "700px", height: "700px", borderRadius: "50%",
        background: "radial-gradient(circle, rgba(0,240,255,0.05) 0%, transparent 65%)",
        top: "50%", left: "50%", transform: "translate(-50%, -50%)",
      }} />
      {[
        "top-6 left-6 border-t-2 border-l-2",
        "top-6 right-6 border-t-2 border-r-2",
        "bottom-6 left-6 border-b-2 border-l-2",
        "bottom-6 right-6 border-b-2 border-r-2",
      ].map((cls, i) => (
        <span key={i} className={`absolute w-6 h-6 opacity-20 ${cls}`} style={{ borderColor: "#00F0FF" }} />
      ))}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute w-full h-px opacity-10" style={{
          background: "linear-gradient(90deg, transparent, #00F0FF, transparent)",
          animation: "scan 4s linear infinite", top: 0,
        }} />
      </div>
      <style>{`
        @keyframes scan { 0% { top: 0%; } 100% { top: 100%; } }
        @keyframes flicker { 0%, 100% { opacity: 1; } 92% { opacity: 1; } 93% { opacity: 0.4; } 94% { opacity: 1; } }
        input:-webkit-autofill {
          -webkit-box-shadow: 0 0 0px 1000px #0d0e14 inset !important;
          -webkit-text-fill-color: #e0f7ff !important;
        }
      `}</style>

      {/* Card */}
      <div className="relative w-full max-w-sm mx-4 rounded-2xl overflow-hidden"
        style={{
          backgroundColor: "rgba(13,14,20,0.95)",
          border: "1px solid rgba(0,240,255,0.15)",
          boxShadow: "0 0 0 1px rgba(0,240,255,0.05), 0 32px 80px rgba(0,0,0,0.6), 0 0 60px rgba(0,240,255,0.04)",
        }}
      >
        <div className="h-px w-full" style={{ background: "linear-gradient(90deg, transparent, #00F0FF, transparent)" }} />
        <div className="p-8">
          {/* Logo — unchanged */}
          <div className="flex flex-col items-center mb-10">
            <div className="relative mb-5">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center"
                style={{ background: "linear-gradient(135deg, #001a1c, #003d42)", border: "1px solid rgba(0,240,255,0.25)", boxShadow: "0 0 30px rgba(0,240,255,0.12)" }}>
                <svg className="w-8 h-8" fill="none" stroke="#00F0FF" strokeWidth={1.5} viewBox="0 0 24 24">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              </div>
              <div className="absolute -inset-1 rounded-2xl animate-ping opacity-10" style={{ border: "1px solid #00F0FF" }} />
            </div>
            <h1 className="text-2xl font-bold tracking-widest uppercase"
              style={{ color: "#e0f7ff", letterSpacing: "0.15em", animation: "flicker 6s infinite" }}>
              ARGUS
            </h1>
            <p className="text-xs mt-1 text-center tracking-widest" style={{ color: "#00F0FF", opacity: 0.7 }}>
              Automated Reconnaissance and Global Universal Surveillance
            </p>
            <div className="flex items-center gap-2 mt-4 px-3 py-1.5 rounded-full text-xs"
              style={{ backgroundColor: "rgba(0,240,255,0.05)", border: "1px solid rgba(0,240,255,0.12)", color: "#00F0FF" }}>
              <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: "#00F0FF" }} />
              SYSTEM ONLINE
            </div>
          </div>

          {/* Fields */}
          <div className="space-y-4">
            <div>
              <label className="block text-xs tracking-widest uppercase mb-2" style={{ color: "rgba(0,240,255,0.5)" }}>
                Operator ID
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" style={{ color: "rgba(0,240,255,0.3)" }}>
                    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 11a4 4 0 100-8 4 4 0 000 8z" />
                  </svg>
                </div>
                <input
                  type="text"
                  value={userId}
                  onChange={e => setUserId(e.target.value)}
                  placeholder="OP..."
                  className="w-full pl-9 pr-4 py-2.5 rounded-lg text-xs outline-none transition-all duration-200"
                  style={{ backgroundColor: "#0d0e14", border: "1px solid rgba(0,240,255,0.12)", color: "#e0f7ff", caretColor: "#00F0FF", letterSpacing: "0.05em" }}
                  onFocus={e => e.target.style.borderColor = "rgba(0,240,255,0.45)"}
                  onBlur={e => e.target.style.borderColor = "rgba(0,240,255,0.12)"}
                />
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="text-xs tracking-widest uppercase" style={{ color: "rgba(0,240,255,0.5)" }}>Auth Key</label>
                <a href="#" className="text-xs" style={{ color: "rgba(0,240,255,0.4)" }}>Reset?</a>
              </div>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" style={{ color: "rgba(0,240,255,0.3)" }}>
                    <path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && handleLogin()}
                  placeholder="••••••••"
                  className="w-full pl-9 pr-4 py-2.5 rounded-lg text-xs outline-none transition-all duration-200"
                  style={{ backgroundColor: "#0d0e14", border: "1px solid rgba(0,240,255,0.12)", color: "#e0f7ff", caretColor: "#00F0FF" }}
                  onFocus={e => e.target.style.borderColor = "rgba(0,240,255,0.45)"}
                  onBlur={e => e.target.style.borderColor = "rgba(0,240,255,0.12)"}
                />
              </div>
            </div>

            {/* Error message */}
            {error && (
              <div className="text-xs px-3 py-2 rounded-lg tracking-wide"
                style={{ backgroundColor: "rgba(255,50,50,0.08)", border: "1px solid rgba(255,50,50,0.2)", color: "#ff6b6b" }}>
                ⚠ {error}
              </div>
            )}

            <button
              onClick={handleLogin}
              disabled={loading}
              className="w-full py-3 rounded-lg text-xs font-bold tracking-widest uppercase mt-2 transition-all duration-200 hover:brightness-110 active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                background: "linear-gradient(135deg, #003d42, #005f66)",
                color: "#00F0FF",
                border: "1px solid rgba(0,240,255,0.3)",
                boxShadow: "0 0 20px rgba(0,240,255,0.1), inset 0 1px 0 rgba(0,240,255,0.1)",
                letterSpacing: "0.2em",
              }}
            >
              {loading ? "AUTHENTICATING..." : "AUTHENTICATE"}
            </button>
          </div>

          <div className="flex items-center justify-center gap-2 mt-6 pt-5 text-xs tracking-widest"
            style={{ borderTop: "1px solid rgba(0,240,255,0.06)", color: "rgba(0,240,255,0.2)" }}>
            v1.0.0
          </div>
        </div>
      </div>
    </main>
  );
}